import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { Box } from "@mui/material";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { PageHeader } from "components/ui/PageHeader";

import { TemplatePreview } from "../../../shared/components/TemplatePreview/TemplatePreview";
import layout from "../../../templatesTable.module.css";
import { useCreateEmailTemplate } from "../../hooks/useCreateEmailTemplate";
import { useDeleteEmailTemplate } from "../../hooks/useDeleteEmailTemplate";
import { useEmailTemplatesAll } from "../../hooks/useEmailTemplatesAll";
import { EmailTemplateModal } from "../EmailTemplateModal";

const columns = [
  {
    key: "name",
    header: "Name",
    width: "minmax(150px, 1fr)",
    render: (row) => row.name,
  },
  {
    key: "subject",
    header: "Subject",
    width: "200px",
    render: (row) => row.subject,
  },
  {
    key: "template",
    header: "Template Content",
    width: "780px",
    render: (row) => <TemplatePreview html={row.template} stripImages />,
  },
];

export const EmailTemplatesTable = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: templates = [], isLoading } = useEmailTemplatesAll();
  const { mutate: saveTemplate } = useCreateEmailTemplate();
  const { mutate: deleteTemplate } = useDeleteEmailTemplate();

  const handleAddTemplate = () => {
    setSelectedTemplate(null);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (row) => {
    setSelectedTemplate(row);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleSubmit = (formValues) => {
    const isEditing = !!selectedTemplate;
    const payload = {
      name: formValues.name,
      subject: formValues.subject,
      template: formValues.template,
      ...(isEditing ? { id: selectedTemplate.id } : {}),
    };

    saveTemplate(payload, {
      onSuccess: () => {
        toast.success(
          isEditing
            ? "Email template updated successfully."
            : "Email template saved successfully."
        );
        setIsModalOpen(false);
        setSelectedTemplate(null);
      },
      onError: (error) => {
        toast.error(error.message);
        setIsModalOpen(false);
        setSelectedTemplate(null);
      },
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteTemplate(deleteTarget.id, {
      onSuccess: () => toast.success("Email template deleted successfully."),
      onError: (error) => toast.error(error.message),
    });
    setDeleteTarget(null);
  };

  const rowActions = [
    {
      label: "Edit",
      icon: Pencil,
      primary: true,
      onClick: (row) => handleEditTemplate(row),
    },
    {
      label: "Delete",
      icon: Trash2,
      primary: true,
      onClick: (row) => setDeleteTarget(row),
    },
  ];

  return (
    <Box className={layout.tableLayout}>
      <PageHeader title="Email Templates">
        <Button
          variant="text"
          endIcon={Plus}
          data-testid="add-email-template-button"
          onClick={handleAddTemplate}
        >
          Add Email Template
        </Button>
      </PageHeader>

      <DataTable
        columns={columns}
        data={templates}
        isLoading={isLoading}
        emptyTitle="No email templates found"
        emptyText="There are no email templates to display."
        rowsPerPage={20}
        rowActions={rowActions}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Email Template"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        confirmLabel="Delete"
        confirmVariant="primary"
      />

      <EmailTemplateModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        editTemplate={selectedTemplate}
      />
    </Box>
  );
};
